import { ref, shallowReadonly } from 'vue';
import { CalculatedValues } from '@/ts/map/calculationFunctions';
import { LeafNode, ParentNode } from '@/ts/composables/useMarketCarpetData';

export interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  height: number;
  depth: number;
  parent: Box | null;
  data: LeafNode | ParentNode;
}

export interface LeafBoxHoverData {
  symbol: string;
  name: string;
  calculatedValues: CalculatedValues;
  mouseEvent: MouseEvent;
  parent: Box | null;
}

export interface ParentBoxHoverData {
  name: string;
  mouseEvent: MouseEvent;
}

export function isLeafBoxHoverData(data: LeafBoxHoverData | ParentBoxHoverData | null): data is LeafBoxHoverData {
  return data !== null && 'calculatedValues' in data;
}

const DEFAULT_IS_HEADER_SHOWN = true;
const DEFAULT_IS_SHARP_CHART_SHOWN = true;

// Factory for creating instance-scoped hover box state
export function createHoverBox(options?: { showInfoHover?: boolean; showMiniChart?: boolean }) {
  const hoverData = ref<LeafBoxHoverData | ParentBoxHoverData | null>(null);
  const isHeaderShown = ref(options?.showInfoHover ?? DEFAULT_IS_HEADER_SHOWN);
  const isSharpChartShown = ref(options?.showMiniChart ?? DEFAULT_IS_SHARP_CHART_SHOWN);

  function setLeafBoxHoverData(leaf: LeafNode, mouseEvent: MouseEvent, parent: Box | null) {
    hoverData.value = {
      symbol: leaf.symbol,
      name: leaf.name,
      calculatedValues: leaf.calculatedValues,
      mouseEvent,
      parent
    };
  }

  function setParentBoxHoverData(name: string, mouseEvent: MouseEvent) {
    hoverData.value = { name, mouseEvent };
  }

  function clearHoverData() {
    hoverData.value = null;
  }

  return {
    hoverData: shallowReadonly(hoverData),
    setLeafBoxHoverData,
    setParentBoxHoverData,
    clearHoverData,
    isHeaderShown,
    isSharpChartShown
  };
}

export {
  DEFAULT_IS_HEADER_SHOWN,
  DEFAULT_IS_SHARP_CHART_SHOWN
}
